import { useMutation, useQuery } from '@apollo/client';
import { GET_TASK_LIST, TicketClient } from '@nile/graphql';
import {
  AppContext,
  Loader,
  HeaderTag,
  getTranslatedText,
  ProductWorklogs,
  BackgroundIcon,
  AccessControl,
  useUserMSPCheckHook,
  ALL_MODULES,
  getParsedCrudData,
  isAuthorised,
  useActivityTracker,
  PSA_CONSTANTS,
  useIsAuthorisedHook,
  Column,
  COMMON_CONSTANTS
} from '@nile/components';
import { Card, Heading, TextStyle } from '@superops/ui-design-system';
import envConfig from '@nile/home/src/Config/env';
import ProjectContext from '@nile/projects/src/Contexts/ProjectContext';
import { ContentWrapper } from 'sopslibrary';
import {
  MyTasks,
  OtherTasksProgressCard,
  ProjectInsightCard
} from '@nile/projects/src/Components/Cards';
import MilestoneProgressCard from '@nile/projects/src/Components/Cards/MilestoneProgressCard';
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import styled from 'styled-components';
import TicketSchedulingCard from '@nile/mangifera/src/Containers/CardContainers/TicketSchedulingCard.js';
import { useCheckFeatureGating } from '@nile/components/src/HOC/withPlanFeatureGating';
import { SuperSortable } from '@nile/components/src/SuperSortable/SuperSortable';
import { pick, sortBy } from 'lodash';
import { UPDATE_MILESTONES } from '@nile/graphql/src/Mutation/task';
import { WrapperTag } from '@nile/components/src/Components/InlineEdit/Components/InlineSelect';
import { SVGIcon } from '@superops/super-icons';
import { useCheckIsItProduct } from '@nile/components/src/Utils/helpers';

const LoadWrapper = styled.div`
  height: calc(100vh - 244px);
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProjectContentWrapper = styled(ContentWrapper)`
  padding: 0 !important;
  gap: 0 !important;
`;

const InsightsContainer = (props) => {
  const { projectId } = props.project;
  const { intl } = props;
  const appContext = useContext(AppContext);
  const projectContext = useContext(ProjectContext);
  const { user } = appContext;
  const flags = useFlags();
  const [tasks, setTasks] = useState([]);
  const [mytasks, setMyTasks] = useState([]);
  const isMSP = useUserMSPCheckHook();

  const [milestones, setMilestones] = useState(props.project.milestones);

  useEffect(() => {
    setMilestones(props.project.milestones);
  }, [props.project.milestones]);

  const { loading, error, data, refetch } = useQuery(GET_TASK_LIST, {
    variables: {
      listInfo: {
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'project.projectId',
              operator: 'is',
              value: projectId
            },
            {
              attribute: 'milestone.milestoneId',
              operator: 'isEmpty',
              value: null
            }
          ]
        },
        sort: [
          {
            attribute: 'taskOrder',
            order: 'ASC',
            emptyAtLast: true
          }
        ]
      }
    },
    onCompleted(newData) {
      if (newData?.getTasks) {
        setTasks(newData.getTasks.tasks);
      }
    }
  });

  const {
    loading: myTasksLoading,
    error: myTasksError,
    data: myTasksData
  } = useQuery(GET_TASK_LIST, {
    variables: {
      listInfo: {
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'project.projectId',
              operator: 'is',
              value: projectId
            },
            {
              attribute: 'technician.userId',
              operator: 'is',
              value: user.userId
            }
          ]
        }
      }
    },
    onCompleted(newData) {
      if (newData?.getTasks) {
        setMyTasks(newData.getTasks.tasks);
      }
    }
  });

  useEffect(() => {
    TicketClient.query({
      query: GET_TASK_LIST,
      variables: {
        listInfo: {
          condition: {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'project.projectId',
                operator: 'is',
                value: projectId
              },
              {
                attribute: 'technician.userId',
                operator: 'is',
                value: user.userId
              }
            ]
          }
        }
      },
      fetchPolicy: 'network-only'
    }).then((res) => {
      if (res?.data?.getTasks) {
        setMyTasks(res.data.getTasks.tasks);
      }
    });

    TicketClient.query({
      query: GET_TASK_LIST,
      variables: {
        listInfo: {
          condition: {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'project.projectId',
                operator: 'is',
                value: projectId
              },
              {
                attribute: 'milestone.milestoneId',
                operator: 'isEmpty',
                value: null
              }
            ]
          },
          sort: [
            {
              attribute: 'taskOrder',
              order: 'ASC',
              emptyAtLast: true
            }
          ]
        }
      },
      fetchPolicy: 'network-only'
    }).then((res) => {
      if (res?.data?.getTasks) {
        setTasks(res.data.getTasks.tasks);
      }
    });
  }, [props.project]);

  const sortedMilestones = useMemo(() => sortBy(milestones, 'milestoneOrder'), [
    milestones
  ]);

  const [isReorderingMilestone, setIsReorderingMilestone] = useState(false);

  const [updateMilestones] = useMutation(UPDATE_MILESTONES);

  function onMilestoneMutation(milestonesList) {
    setIsReorderingMilestone(true);
    updateMilestones({
      variables: {
        input: milestonesList.map((milestone) =>
          pick(milestone, ['milestoneId', 'name', 'milestoneOrder'])
        )
      }
    }).finally(() => setIsReorderingMilestone(false));
  }

  function handleMilestoneUpdate(key, id) {
    props.handleUpdate(key, id);

    if (key === 'deleteMilestone') {
      const deletedList = sortBy(milestones, 'milestoneOrder')
        .filter((m) => m.milestoneId !== id)
        .map((m, i) => ({ ...m, milestoneOrder: i + 1 }));

      const { updatedItems } = getParsedCrudData(
        milestones,
        deletedList.map((m, i) => ({ ...m, milestoneOrder: i + 1 })),
        'milestoneId'
      );

      onMilestoneMutation(updatedItems);
    }
  }

  const isMilestoneEditAuthorized = isAuthorised(
    projectContext.features,
    'milestone.edit'
  );

  if (loading || myTasksLoading) {
    return (
      <LoadWrapper>
        <Loader />
      </LoadWrapper>
    );
  }

  return (
    <div className="bg-white scroll full-height full-width">
      <ProjectContentWrapper
        className="full-height acstart overflow-auto flex"
        bgcolor="var(--sops-lightpurple-to-black-bg)"
      >
        <div
          className={`flex col g40 pt20 pb20 fg1 fb0 full-height bg-white x-hidden overflow-y-auto trans-white-border-right hideScrollBar ${isReorderingMilestone &&
            'disable-actions'}`}
        >
          <AccessControl
            productToCheck={COMMON_CONSTANTS.MSP}
            render={<ProjectsContractField project={props.project} />}
          />

          {!(
            milestones.length > 0 ||
            mytasks.length > 0 ||
            tasks.length > 0
          ) && (
            <div className="flex col center jcenter full-height pr-20 pl20">
              <BackgroundIcon
                url={`url(${envConfig.imageRoot}svg/vacation.svg)`}
                height="100px"
                width="150px"
              />
              <div className="text-align-centr">
                <TextStyle variation="subdued">
                  <FormattedMessage id="project.details.noTasksOrMilestones" />
                </TextStyle>
              </div>
            </div>
          )}
          <AccessControl
            reference={projectContext?.features}
            roleToCheck={{
              roles: ['projectWorklog.add', 'serviceCatalog.view']
            }}
            render={
              <ProductWorklogs project={props.project} module="PROJECT" />
            }
          />
          {mytasks.length > 0 && (
            <MyTasks
              tasks={mytasks}
              setTasks={setMyTasks}
              project={props.project}
              setProject={props.setProject}
            />
          )}
          <SuperSortable
            onDragEnd={(reorderedList) => {
              const newList = reorderedList.map((m, index) => ({
                ...m,
                milestoneOrder: index + 1
              }));

              setMilestones(newList);

              const { updatedItems } = getParsedCrudData(
                milestones,
                newList,
                'milestoneId'
              );

              if (!updatedItems.length) return;

              // eslint-disable-next-line react-hooks/rules-of-hooks
              useActivityTracker({
                event:
                  PSA_CONSTANTS.MIX_PANEL_EVENTS_FOR_REORDER.PROJECT_DETAILS
                    .MILESTONE
              });

              onMilestoneMutation(updatedItems);
            }}
            list={sortedMilestones}
          >
            {sortedMilestones.map((milestone) => (
              <MilestoneProgressCard
                milestone={milestone}
                project={props.project}
                handleUpdate={handleMilestoneUpdate}
                setProject={props.setProject}
                taskTemplates={props?.taskTemplates}
                disableDrag={!isMilestoneEditAuthorized}
              />
            ))}
          </SuperSortable>

          {tasks.length > 0 && (
            <OtherTasksProgressCard
              tasks={tasks}
              setTasks={setTasks}
              project={props.project}
              setProject={props.setProject}
              taskTemplates={props?.taskTemplates}
            />
          )}
        </div>
        <div className="flex col center g10 fg1 fb0 full-height overflow-auto">
          <ProjectInsightCard {...props} />
        </div>
        {/* <div>
          <HeaderTag
            title={getTranslatedText(intl, 'project.details.insights')}
            isBgStrip
            isNewLayout
          />
        </div> */}
      </ProjectContentWrapper>
    </div>
  );
};

function ProjectsContractField({ project }) {
  const contractName = project?.contract?.billableContract?.chargeItem?.name;

  return (
    <Column gap={10} className="pl-20">
      <TextStyle variation="subdued">
        <FormattedMessage id="common.contract" />
      </TextStyle>
      <div className={'cell flex center full-height'}>
        <div className="mr-10">
          <SVGIcon name="Contract" />
        </div>
        <WrapperTag
          className="ellipsis self-center flex center"
          title={contractName}
        >
          {contractName || 'NA'}
        </WrapperTag>
      </div>
    </Column>
  );
}

export default injectIntl(InsightsContainer);
